import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { Container, StatusPill, EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { Heart, Repeat, XCircle, Receipt, Loader2, FileText, Mail, Printer } from "lucide-react";
import { toast } from "sonner";

function TaxReceiptCard() {
  const now = new Date();
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const [year, setYear] = useState(years[0]);
  const [data, setData] = useState(null);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/donations/tax-summary/${year}`).then((r) => setData(r.data)).catch(() => setData({ count: 0, items: [], total: 0 }));
  }, [year]);

  const emailReceipt = async () => {
    setEmailing(true);
    try {
      const { data: res } = await api.post(`/donations/tax-receipt/${year}/send`);
      toast.success(res.sent ? `Emailed your ${year} summary to ${res.email}.` : `Prepared your ${year} summary (email delivery is not enabled yet).`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setEmailing(false); }
  };

  const printReceipt = () => {
    if (!data || !data.count) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to download your receipt."); return; }
    const rows = data.items.map((it) =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #eee">${String(it.date || "").slice(0, 10)}</td>
       <td style="padding:6px 8px;border-bottom:1px solid #eee">${it.campaign_title || ""}${it.is_nonprofit ? " (nonprofit)" : ""}</td>
       <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">$${(it.amount || 0).toFixed(2)}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>FightFund ${year} Donation Summary</title></head>
      <body style="font-family:Arial,sans-serif;color:#143257;max-width:640px;margin:24px auto;padding:0 16px">
        <h1 style="font-size:22px">Fight<span style="color:#C62828">Fund</span></h1>
        <h2 style="font-size:18px">${year} Donation Summary</h2>
        <p style="color:#374151;font-size:14px">${data.donor_name || ""} &middot; ${data.donor_email || ""}</p>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:12px">
          <tr style="background:#f3f4f6"><th style="padding:6px 8px;text-align:left">Date</th><th style="padding:6px 8px;text-align:left">Campaign</th><th style="padding:6px 8px;text-align:right">Amount</th></tr>
          ${rows}
          <tr><td colspan="2" style="padding:10px 8px;font-weight:800">Total donated in ${year}</td><td style="padding:10px 8px;text-align:right;font-weight:800">$${(data.total || 0).toFixed(2)}</td></tr>
        </table>
        <p style="color:#6b7280;font-size:12px;margin-top:16px">Of this, $${(data.nonprofit_total || 0).toFixed(2)} went to verified U.S. nonprofit campaigns and may be tax-deductible. This is a summary of your giving, not an official tax document. Please consult a tax professional.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="mt-6 rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="tax-receipt-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF6FF] text-ff-blue"><FileText className="h-5 w-5" /></span>
          <div>
            <div className="font-heading text-base font-bold text-ff-blue">Year-end tax receipt</div>
            <p className="text-xs text-ff-medium">A summary of everything you donated in a year — download it or have it emailed to you.</p>
          </div>
        </div>
        <select data-testid="tax-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-lg border border-ff-border bg-white px-3 py-2 text-sm font-semibold text-ff-blue focus:outline-none focus:ring-2 focus:ring-ff-blue">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {data === null ? (
        <div className="mt-4 text-sm text-ff-medium">Loading {year} summary…</div>
      ) : data.count === 0 ? (
        <div className="mt-4 rounded-lg bg-[#F3F4F6] px-3 py-3 text-sm text-ff-medium" data-testid="tax-empty">No donations recorded in {year}.</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-ff-border p-3"><div className="text-xs text-ff-medium">Donations</div><div className="font-heading text-xl font-extrabold text-ff-blue" data-testid="tax-count">{data.count}</div></div>
            <div className="rounded-lg border border-ff-border p-3"><div className="text-xs text-ff-medium">Total given</div><div className="font-heading text-xl font-extrabold text-ff-green" data-testid="tax-total">{money(data.total)}</div></div>
            <div className="rounded-lg border border-ff-border p-3"><div className="text-xs text-ff-medium">Nonprofit (deductible)</div><div className="font-heading text-xl font-extrabold text-ff-blue" data-testid="tax-nonprofit">{money(data.nonprofit_total)}</div></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={printReceipt} data-testid="tax-download-button" className="bg-ff-blue text-white hover:bg-ff-blue/90">
              <Printer className="mr-2 h-4 w-4" /> Download / Print
            </Button>
            <Button onClick={emailReceipt} disabled={emailing} variant="outline" data-testid="tax-email-button" className="border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
              {emailing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Email me this receipt
            </Button>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-ff-medium">Personal-cause gifts are generally not tax-deductible; only donations to verified nonprofit campaigns may qualify. Consult a tax professional.</p>
        </>
      )}
    </div>
  );
}

export default function MyDonations() {
  const [donations, setDonations] = useState(null);
  const [canceling, setCanceling] = useState("");

  const load = () => api.get("/donations/mine/list").then((r) => setDonations(r.data)).catch(() => setDonations([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    setCanceling(id);
    try {
      await api.post(`/donations/${id}/cancel-subscription`);
      toast.success("Monthly donation canceled.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCanceling(""); }
  };

  return (
    <div className="bg-[#F3F4F6]">
      <Seo title="My Donations" />
      <Container className="py-10">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">My Donations</h1>
        <p className="mt-1 text-sm text-ff-medium">Your donation history and monthly recurring gifts.</p>

        <TaxReceiptCard />

        {donations === null ? (
          <div className="mt-8"><InlineLoader label="Loading your donations" /></div>
        ) : donations.length === 0 ? (
          <div className="mt-8">
            <EmptyState icon={Heart} title="No donations yet"
              message="When you donate to a campaign, it will appear here."
              action={<Button asChild className="bg-ff-action text-white"><Link to="/browse">Browse campaigns</Link></Button>}
              testid="my-donations-empty" />
          </div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="my-donations-list">
            {donations.map((d) => {
              const recurring = d.donation_type === "recurring";
              const active = recurring && d.recurring_status !== "canceled";
              return (
                <div key={d.id} className="flex flex-col gap-3 rounded-xl border border-ff-border bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-ff-blue">{money(d.amount)}</span>
                      {recurring && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${active ? "bg-[#ECFDF3] text-ff-green" : "bg-[#F3F4F6] text-ff-medium"}`}>
                          <Repeat className="h-3 w-3" /> {active ? "Monthly" : "Canceled"}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-sm text-ff-dark">
                      {d.campaign_slug ? <Link to={`/fundraisers/${d.campaign_slug}`} className="hover:text-ff-red">{d.campaign_title}</Link> : d.campaign_title}
                    </div>
                    <div className="mt-0.5 text-xs text-ff-medium">{d.paid_at ? new Date(d.paid_at).toLocaleDateString() : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={d.status === "receipt_issued" ? "released" : d.status} />
                    {recurring && active && (
                      <Button size="sm" variant="outline" disabled={canceling === d.id} data-testid={`cancel-recurring-${d.id}`}
                        onClick={() => cancel(d.id)} className="border-ff-red text-ff-red hover:bg-ff-red hover:text-white">
                        {canceling === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><XCircle className="mr-1 h-3.5 w-3.5" /> Cancel</>}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Container>
    </div>
  );
}
